/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { When } from "react-if";
import { Button, Label, theme } from "@nordcloud/gnui";
import {
  CostMappingAlgorithm,
  EnvCostShare,
  ResourceCostSplitRule,
} from "~/generated/graphql";
import { isNotEmpty, isNotNil } from "~/tools";
import { AppEnvSelector } from "~/views/estate/components/AppEnvSelector";
import { SELECTOR_TYPE } from "~/views/estate/components/AppEnvSelector/types";
import { ApplicationOption } from "~/views/estate/components/types";
import { optionsNames } from "~/views/settings/CostSplittingRulesPage/CostSplittingDefs";
import { CostSplittingEnvSelector } from "~/views/settings/CostSplittingRulesPage/CostSplittingEnvSelector";
import { useAppsEnvsAlgorithmSelections } from "../hooks";
import { getAttachmentSelections, isAttachmentDisabled } from "../utils";
import { CostSplitAlgorithmSelector } from "./CostSplitAlgorithmSelector";

export type MultipleEnvironmentFormData = {
  envCosts: EnvCostShare[];
  selectedAlgorithm: CostMappingAlgorithm;
};

type ApplicationEnvFormProps = {
  applications: ApplicationOption[];
  onSubmit: (data: MultipleEnvironmentFormData) => Promise<void>;
  costSplitRules?: ResourceCostSplitRule;
  saveSelections?: boolean;
};

export function MultipleEnvironmentForm({
  applications,
  onSubmit,
  costSplitRules,
  saveSelections = true,
}: ApplicationEnvFormProps) {
  const [isOpenEnvAllocatedSidebar, setEnvAllocatedSidebar] =
    React.useState(false);

  const {
    selectedApplications,
    selectedEnvironments,
    algorithm,
    setAlgorithm,
    selectAppsAndEnvs,
  } = useAppsEnvsAlgorithmSelections(
    saveSelections ? costSplitRules : undefined
  );

  const getEnvCosts = () =>
    costSplitRules?.settings?.environments
      ? costSplitRules?.settings?.environments?.map(({ id, splitValue }) => ({
          id: id ?? "",
          splitValue,
        }))
      : [];

  const primaryEnvCostsSelection = React.useMemo(
    () => (saveSelections ? getEnvCosts() : []),
    [saveSelections]
  );

  const { register, handleSubmit, setValue, watch } = useForm<{
    envCosts: EnvCostShare[];
    selectedAlgorithm: CostMappingAlgorithm;
  }>({
    defaultValues: {
      envCosts: primaryEnvCostsSelection,
      selectedAlgorithm: saveSelections ? costSplitRules?.algorithm : undefined,
    },
  });

  const { envCosts, selectedAlgorithm } = watch();

  React.useEffect(() => {
    register("envCosts", { required: true });
    register("selectedAlgorithm", { required: true });
  }, [register]);

  React.useEffect(() => {
    calculateEnvCosts();
  }, [selectedApplications, selectedEnvironments]);

  const isDisabled = isAttachmentDisabled({
    attachmentSelections: getAttachmentSelections(envCosts, selectedAlgorithm),
  });

  const applicationsToDivideCost = applications
    ?.map((app) => {
      if (selectedApplications.includes(app?.nid ?? "")) {
        return app;
      }
      if (!app.environments) {
        return null;
      }
      return {
        ...app,
        environments: app.environments
          .map((env) => {
            if (selectedEnvironments.includes(env?.nid ?? "")) {
              return env;
            }
            return null;
          })
          .filter(isNotNil)
          .filter((item) => item.nid),
      };
    })
    .filter(isNotNil)
    .filter((item) => item.environments && isNotEmpty(item.environments));

  const onSettingsClick = () => {
    setEnvAllocatedSidebar(true);
  };

  const onAlgorithmSelect = (optionName: CostMappingAlgorithm) => {
    setAlgorithm(optionName);
    setValue("selectedAlgorithm", optionName);
    if (optionName === CostMappingAlgorithm.Manual) {
      setEnvAllocatedSidebar(true);
    } else {
      calculateEnvCosts(true);
    }
  };

  const calculateEnvCosts = (setToZero?: boolean) => {
    const newEnvCosts = applicationsToDivideCost
      .flatMap((app) => app.environments)
      .map((env) => {
        const splitValue =
          primaryEnvCostsSelection?.find((item) => item.id === env.id) &&
          !setToZero
            ? primaryEnvCostsSelection?.find((item) => item.id === env.id)
                ?.splitValue
            : undefined;

        return {
          id: env?.nid ?? "",
          splitValue,
        };
      });

    setValue("envCosts", newEnvCosts);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Label required name="Applications and Environments" />
      <AppEnvSelector
        onlyEnvironments
        applications={applications}
        loading={false}
        selectedApplications={selectedApplications}
        selectedEnvironments={selectedEnvironments}
        selectionType={SELECTOR_TYPE.checkbox}
        onChange={selectAppsAndEnvs}
        onClear={() => selectAppsAndEnvs([], [])}
      />
      <When condition={isNotEmpty(applicationsToDivideCost)}>
        <Label
          required
          name="Cost Splitting Algorithm"
          style={{ marginTop: theme.spacing.spacing03 }}
        />
        <CostSplitAlgorithmSelector
          optionsNames={optionsNames}
          algorithm={algorithm}
          savedSelections={isNotEmpty(envCosts)}
          onSettingsClick={onSettingsClick}
          onAlgorithmSelect={onAlgorithmSelect}
        />
      </When>
      <When condition={isOpenEnvAllocatedSidebar}>
        <CostSplittingEnvSelector
          saveDataOnly
          isOpen={isOpenEnvAllocatedSidebar}
          isOpenAllocatedMethodSidebar={false}
          algorithm={algorithm}
          applications={applicationsToDivideCost}
          value={{ allEnvironments: false, environments: [] }}
          envCostShares={envCosts}
          setEnvCostShares={(data) => setValue("envCosts", data)}
          onClose={() => setEnvAllocatedSidebar(false)}
        />
      </When>
      <Button
        disabled={isDisabled}
        type="submit"
        style={{ marginTop: theme.spacing.spacing04 }}
      >
        Attach Resource
      </Button>
    </form>
  );
}
